import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Client, ClientTransaction } from "../../types";

interface ExportPDFProps {
  client: Client;
  transactions: ClientTransaction[];
}

export function exportClientTransactionsPDF({
  client,
  transactions,
}: ExportPDFProps) {
  if (!client) return;
  const doc = new jsPDF();
  doc.setFontSize(20);
  doc.text(`Transactions with ${client.name}`, 14, 18);

  // Table columns
  const columns = [
    { header: "Date", dataKey: "date" },
    { header: "Type", dataKey: "type" },
    { header: "Amount (INR)", dataKey: "amount" },
    { header: "Description", dataKey: "description" },
    { header: "Payment Method", dataKey: "paymentMethod" },
    { header: "Category", dataKey: "category" },
    { header: "Notes", dataKey: "notes" },
  ];

  // Helper for Indian number formatting (lakhs/crores, no symbol)
  function formatINR(num: number) {
    // Format as 12,34,567.89
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Table rows
  const rows = transactions.map((tx) => ({
    date: tx.date ? new Date(tx.date).toLocaleDateString("en-IN") : "",
    type: tx.type.charAt(0).toUpperCase() + tx.type.slice(1),
    amount: formatINR(tx.amount),
    description: tx.description || "",
    paymentMethod: tx.paymentMethod || "",
    category: tx.category || "",
    notes: tx.notes || "",
  }));

  autoTable(doc, {
    startY: 28,
    head: [columns.map((col) => col.header)],
    body: rows.map((row) => columns.map((col) => (row as any)[col.dataKey])),
    styles: { fontSize: 11 },
    headStyles: {
      fillColor: [13, 148, 136], // teal
      textColor: 255, // white
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [240, 255, 255] },
    theme: "striped",
    margin: { left: 14, right: 14 },
  });

  // Summary Table
  const finalY =
    ((doc as any).lastAutoTable && (doc as any).lastAutoTable.finalY) ||
    28 + rows.length * 10;
  autoTable(doc, {
    startY: finalY + 10,
    head: [["Summary", "Amount (INR)"]],
    body: [
      ["Debit", formatINR(client.totalGiven)],
      ["Credit", formatINR(client.totalTaken)],
      [
        "Net Balance",
        `${client.effectiveBalance >= 0 ? "+" : "-"}${formatINR(Math.abs(client.effectiveBalance))}`,
      ],
    ],
    styles: { fontSize: 13 },
    headStyles: {
      fillColor: [13, 148, 136],
      textColor: 255,
      fontStyle: "bold",
    }, // teal
    bodyStyles: { fontStyle: "bold" },
    didParseCell: function (data) {
      // Header row: set both header cells to white text
      if (
        data.row.section === "head" &&
        (data.column.index === 0 || data.column.index === 1)
      ) {
        data.cell.styles.textColor = 255; // white
      }
      // Debit row (first row)
      if (
        data.row.index === 0 &&
        data.column.index === 1 &&
        data.row.section === "body"
      ) {
        data.cell.styles.textColor = [239, 68, 68]; // red
      }
      // Credit row (second row)
      if (
        data.row.index === 1 &&
        data.column.index === 1 &&
        data.row.section === "body"
      ) {
        data.cell.styles.textColor = [13, 148, 136]; // green/teal
      }
      // Net Balance row (third row)
      if (
        data.row.index === 2 &&
        data.column.index === 1 &&
        data.row.section === "body"
      ) {
        data.cell.styles.textColor =
          client.effectiveBalance >= 0 ? [13, 148, 136] : [239, 68, 68];
      }
    },
    margin: { left: 14, right: 14 },
    theme: "grid",
  });

  doc.save(`Transactions_${client.name}.pdf`);
}
